import { useState, useMemo, useEffect } from 'react';
import { useSchemaViewerContext } from './SchemaContext';
import { schemaRegistry, SchemaRegistry } from './definitions/schemaRegistry';

type GroupedSchemas = { [key: string]: SchemaRegistry[] };

export const LeftPanel = () => {
  const { setSchema, setSelectedRegistry, setError, setFormData } = useSchemaViewerContext();
  const [selectedSchemaType, setSelectedSchemaType] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<string>('');

  // Group schemas by title (type)
  const groupedSchemas: GroupedSchemas = useMemo(() => {
    return schemaRegistry.reduce((acc: GroupedSchemas, registry) => {
      const key = registry.title;
      if (!acc[key]) acc[key] = [];
      acc[key].push(registry);
      return acc;
    }, {});
  }, []);

  // Auto-select latest version when type changes
  useEffect(() => {
    if (selectedSchemaType && groupedSchemas[selectedSchemaType]) {
      const versions = groupedSchemas[selectedSchemaType];
      // Sort by version descending (assuming numeric or semver)
      const latest = versions.slice().sort((a, b) => {
        // Try to parse as number, fallback to string compare
        const aNum = parseFloat(a.version);
        const bNum = parseFloat(b.version);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return bNum - aNum;
        }
        return b.version.localeCompare(a.version);
      })[0];
      setSelectedVersion(latest.version);
    } else {
      setSelectedVersion('');
      setSchema(null);
      setSelectedRegistry(null);
      setError(null);
    }
  }, [selectedSchemaType, groupedSchemas, setSchema, setSelectedRegistry, setError]);

  // Set schema when selection changes
  useEffect(() => {
    if (!selectedSchemaType || !selectedVersion) {
      setSchema(null);
      setSelectedRegistry(null);
      setFormData(null);
      setError(null);
      return;
    }
    const selected = groupedSchemas[selectedSchemaType]?.find(
      (s) => s.version === selectedVersion
    );
    if (selected) {
      // Pass the registry schema unmodified so it matches the precompiled
      // validator's rootSchema (AJV8PrecompiledValidator deep-equals them).
      setSchema(selected.schema);
      setSelectedRegistry(selected);
      setFormData(null);
    } else {
      setSchema(null);
      setSelectedRegistry(null);
      setError('Schema not found');
    }
  }, [selectedSchemaType, selectedVersion, groupedSchemas, setSchema, setSelectedRegistry, setError, setFormData]);

  return (
    <div style={{ flex: '0 0 300px', padding: '1.6rem', backgroundColor: '#2A55C3', color: 'white' }}>
      <div style={{ marginBottom: '0.7rem' }}>
        <svg width="50" id="logo" name="logo" height="50" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M113.33 187.036C92.7447 166.583 92.7447 133.377 113.33 112.924L79.4652 79.2761C61.2614 97.4033 50 122.388 50 149.98C50 205.204 95.0454 250 150.626 250C178.436 250 203.582 238.811 221.786 220.724L187.921 187.076C167.336 207.53 133.956 207.53 113.33 187.036Z" fill="white"></path>
          <path opacity="0.4" d="M150.626 50C146.67 50 142.755 50.2406 138.92 50.6818L250 134.179C242.412 86.4949 200.797 50 150.626 50Z" fill="white"></path>
          <path opacity="0.7" d="M84.3895 74.7844L124.228 104.702C144.41 93.0319 170.686 95.759 187.962 112.924C201.604 126.479 206.165 145.609 201.725 162.934L241.564 192.892C244.551 186.635 246.892 180.018 248.547 173.12L127.336 52.7271C111.15 56.537 96.4985 64.237 84.3895 74.7844Z" fill="white"></path>
        </svg>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="#ffffff" 
          style={{ strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }}
        >
          <title>Link Icon</title>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"></path>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"></path>
        </svg>
        <a style={{ color: 'white', fontWeight: 'bold', fontSize: '14px', textDecoration: 'none', paddingLeft: '4px' }} href="/">Cutover API</a>
      </div>
      {Object.keys(groupedSchemas).length > 0 && (
        <div>
          <div className="form-group">
            <label htmlFor="schema-type-selector">Schema Type</label>
            <select id="schema-type-selector" className="form-control" value={selectedSchemaType} onChange={(e) => setSelectedSchemaType(e.target.value)}>
              <option value="">-- Choose a type --</option>
              {Object.keys(groupedSchemas).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          {selectedSchemaType && (
            <div className="form-group">
              <label htmlFor="schema-version-selector">Version</label>
              <select id="schema-version-selector" className="form-control" value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)}>
                {groupedSchemas[selectedSchemaType].map((schema) => (
                  <option key={schema.id} value={schema.version}>{schema.version}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};